import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { GradeSelectorFilterId } from "../../utils/filterConstants";
import { getCategoryFilterSubCategory } from "../../utils/filterUtils";
import { trackCategoryVisible } from "../../utils/tracking";
import type { PriceRangeType } from "../../utils/types/Model";

export type GradeSelectorCategoryType = { root: string; subCategory?: string; active: boolean };

export type GradeSelectorCategoryRecordType = Record<string, Omit<GradeSelectorCategoryType, "root">>;

export type GradeSelectorFiltersType = {
    filters: {
        [GradeSelectorFilterId.Engine]?: string;
        [GradeSelectorFilterId.WheelDrive]?: string;
        [GradeSelectorFilterId.Transmission]?: string;
        [GradeSelectorFilterId.Colour]?: string[];
        [GradeSelectorFilterId.Price]?: PriceRangeType;
        [GradeSelectorFilterId.Sort]?: string;
        [GradeSelectorFilterId.PopularFeatures]?: string[];
    };
    category: GradeSelectorCategoryRecordType;
};

const gradeSelectorFilterInitialState: GradeSelectorFiltersType = {
    category: {},
    filters: {
        [GradeSelectorFilterId.Engine]: undefined,
        [GradeSelectorFilterId.WheelDrive]: undefined,
        [GradeSelectorFilterId.Transmission]: undefined,
        [GradeSelectorFilterId.Colour]: undefined,
        [GradeSelectorFilterId.Price]: undefined,
    },
};

export const gradeSelectorFiltersSlice = createSlice({
    name: "gradeSelectorFilters",
    initialState: gradeSelectorFilterInitialState,
    reducers: {
        addMultipleChoiceFilterValue(
            state,
            action: PayloadAction<{
                filterId: GradeSelectorFilterId.Colour | GradeSelectorFilterId.PopularFeatures;
                value: string[];
            }>,
        ) {
            const { filterId, value } = action.payload;
            state.filters[filterId] = value;
        },
        addPriceFilterValue(
            state,
            action: PayloadAction<{ filterId: GradeSelectorFilterId.Price; value?: PriceRangeType }>,
        ) {
            const { filterId, value } = action.payload;
            state.filters[filterId] = value;
        },

        addFilterValue(
            state,
            action: PayloadAction<{
                filterId: Exclude<
                    GradeSelectorFilterId,
                    GradeSelectorFilterId.Colour | GradeSelectorFilterId.Price | GradeSelectorFilterId.PopularFeatures
                >;
                value: string;
            }>,
        ) {
            const { filterId, value } = action.payload;
            state.filters[filterId] = value;
        },
        setFilterCategory(state, action: PayloadAction<{ category: string | undefined; allowClose?: boolean }>) {
            const root = action.payload.category?.split("/")[0];
            const { category, allowClose } = action.payload;

            // First check tracking, only then modify state.
            trackCategoryVisible(state, category);

            if (root) {
                // Add or overwrite current category.
                const closeRoot = allowClose && root === action.payload.category && state.category[root]?.active;
                state.category[root] = {
                    active: !closeRoot, // If root category is already open and allowClose is true, close the root category. Else it remains open.
                    subCategory: closeRoot
                        ? undefined // Clear subcategory when root closes
                        : getCategoryFilterSubCategory(root, state, category, !!allowClose),
                };
            }
        },
        clearFilterValue(state, action: PayloadAction<{ filterId: GradeSelectorFilterId }>) {
            const { filterId } = action.payload;

            state.filters[filterId] = undefined;
        },
        clearFilters() {
            return gradeSelectorFilterInitialState;
        },
    },
});

export const {
    addMultipleChoiceFilterValue,
    addFilterValue,
    clearFilterValue,
    setFilterCategory,
    clearFilters,
    addPriceFilterValue,
} = gradeSelectorFiltersSlice.actions;
